import { lazy, Suspense } from 'react'
import { ArrowUpRight, Building2, CheckCircle2, FolderKanban, Loader2, Users } from 'lucide-react'
import { Link } from 'react-router-dom'
import { useProjects } from '@/data/useProjects'
import { useStagesWithFiles } from '@/data/useStagesWithFiles'
import { useUsers } from '@/data/useUsers'
import { computePendingReview } from './pendingReview'
import { PendingReviewPanel } from './PendingReviewPanel'

const OverviewCharts = lazy(() =>
  import('./OverviewCharts').then((m) => ({ default: m.OverviewCharts })),
)

interface StatCardProps {
  label: string
  value: number
  to: string
  icon: React.ComponentType<{ className?: string }>
  accent?: string
}

function StatCard({ label, value, to, icon: Icon, accent = 'text-slate-800' }: StatCardProps) {
  return (
    <Link
      to={to}
      aria-label={`${label}: ${value}`}
      className="group relative overflow-hidden rounded-xl border border-slate-200 bg-white p-4 shadow-sm transition hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
    >
      <div className="flex items-start justify-between">
        <div>
          <div className={`text-3xl font-bold tracking-tight ${accent}`}>{value}</div>
          <div className="mt-1 text-sm text-slate-500 group-hover:text-slate-700">{label}</div>
        </div>
        <span className="rounded-lg bg-slate-100 p-2 text-slate-500 transition group-hover:bg-primary/10 group-hover:text-primary">
          <Icon className="h-5 w-5" />
        </span>
      </div>
      <ArrowUpRight className="absolute bottom-3 right-3 h-4 w-4 text-slate-300 opacity-0 transition group-hover:opacity-100" />
    </Link>
  )
}

export function AdminOverviewPage() {
  const { users } = useUsers()
  const { projects } = useProjects()
  const { stagesWithFiles } = useStagesWithFiles()

  const suppliers = users.filter((user) => user.role === 'supplier').length
  const clients = users.filter((user) => user.role === 'client').length
  const ongoing = projects.filter((project) => project.status === 'ongoing').length
  const completed = projects.filter((project) => project.status === 'completed').length
  const pendingReview = computePendingReview(projects, stagesWithFiles)

  return (
    <div>
      <h1 className="mb-6 text-2xl font-semibold text-slate-800">Overview</h1>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <StatCard label="Users" value={users.length} to="/admin/users" icon={Users} />
        <StatCard
          label="Suppliers"
          value={suppliers}
          to="/admin/users?role=supplier"
          icon={Building2}
        />
        <StatCard label="Clients" value={clients} to="/admin/users?role=client" icon={Building2} />
        <StatCard
          label="Projects"
          value={projects.length}
          to="/admin/projects"
          icon={FolderKanban}
        />
        <StatCard
          label="Ongoing projects"
          value={ongoing}
          to="/admin/projects"
          icon={Loader2}
          accent="text-amber-600"
        />
        <StatCard
          label="Completed projects"
          value={completed}
          to="/admin/projects"
          icon={CheckCircle2}
          accent="text-emerald-600"
        />
      </div>

      <PendingReviewPanel items={pendingReview} />

      <Suspense
        fallback={
          <div className="mt-6 grid gap-6 lg:grid-cols-2">
            <div className="h-[312px] animate-pulse rounded-xl border border-slate-200 bg-slate-100" />
            <div className="h-[312px] animate-pulse rounded-xl border border-slate-200 bg-slate-100" />
          </div>
        }
      >
        <OverviewCharts users={users} projects={projects} />
      </Suspense>
    </div>
  )
}
